"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceQuoteSection from "@/components/service/ServiceQuoteSection";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
const LANDSCAPING_SERVICES = ["Block Paving", "Resinbound", "Tarmac", "Turfing & Artificial Grass", "Patios", "Fencing"];
const PROPERTY_TYPES = ["Residential", "Commercial"];
function LandscapingQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Quote Request Sent" message={successMessage || "Thank you for your inquiry. We'll contact you within 24 hours."} onReset={resetForm} resetLabel="Request Another Quote" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="07700 900000" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postcode" rules={{
        required: "Postcode is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Postcode</FormLabel>
              <FormControl>
                <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Service Required</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select service...</option>
                  {LANDSCAPING_SERVICES.map(service => <option key={service} value={service}>
                      {service}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_type" rules={{
        required: "Please select property type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Property Type</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select type...</option>
                  {PROPERTY_TYPES.map(type => <option key={type} value={type}>
                      {type}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : "Get My Free Quote"}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-2">
        By submitting this form, you agree to our privacy policy. We will never share your details.
      </p>
    </Form>;
}
export default function LandscapingQuote() {
  return <ServiceQuoteSection id="get-a-free-landscaping-quote" eyebrow="GET A FREE QUOTE" heading="Transform Your Outdoor Space Today" intro="Ready to upgrade your outdoor space? Request a free, no-obligation quote for garden landscaping in Southampton, Hampshire & surrounding areas. Matt and the team are here to help." serviceName="Landscaping">
      <FormProvider formId="start_project_form_get_in_touch" sectionName="Get A Free Landscaping Quote" defaultValues={{
      name: "",
      email: "",
      phone: "",
      service_type: "",
      property_type: "",
      postcode: ""
    }} submitText="Get My Free Quote" successMessage="Thank you for your inquiry. We'll contact you within 24 hours.">
        <LandscapingQuoteForm />
      </FormProvider>
    </ServiceQuoteSection>;
}